import { useMemo, useRef } from 'react';
import { ApolloCache, ErrorLike } from '@apollo/client';
import { OnDataOptions } from '@apollo/client/react';
import { logger } from '@lib/services/logger';

// Only the fields the log line reads. Both cards receive the same
// `AiIndexingCoverage` payload, but each through its own app's generated types,
// so the frame is matched structurally instead of importing either of them —
// `indexingState` is a string enum, hence `string`.
export interface AiIndexingCoverageFrame {
  indexed: number;
  skipped: number;
  stale: number;
  pending: number;
  failed: number;
  retryingSoon: number;
  retryingDaily: number;
  parked: number;
  indexingState: string;
  indexingInFlight: number;
  estimatedReindexSeconds: number;
}

type Args<TFrame> = {
  // Names the card in the log lines — the two subscriptions are indistinguishable
  // otherwise.
  label: string;
  // Where the frame lands, which is the only part that differs between the two
  // cards: the own-company card owns a whole query, the system card owns one
  // field of the viewed company. Takes the cache rather than the client so the
  // same writer also serves the retry-parked mutation's `update`.
  onFrame: (cache: ApolloCache, coverage: TFrame) => void;
};

/**
 * The `onData`/`onError` pair for a live document-indexing coverage
 * subscription, shared by the own-company card (admin) and the viewed-company
 * card (system).
 *
 * Frames are written straight through: the backend already debounces them per
 * company (1 s, leading + trailing), so there is nothing left for the client to
 * coalesce, and a client-side throttle would only put a delayed write behind the
 * unthrottled ones the retry-parked mutation and the focus refetch perform.
 */
export default function useAiIndexingCoverageFrames<
  TFrame extends AiIndexingCoverageFrame,
>({ label, onFrame }: Args<TFrame>) {
  // Call sites pass an inline writer, so it is read through a ref to keep the
  // returned handlers — and with them the subscription's options — stable.
  const onFrameRef = useRef(onFrame);
  onFrameRef.current = onFrame;

  return useMemo(
    () => ({
      onData: ({ client, data }: OnDataOptions<{ data?: TFrame | null }>) => {
        const coverage = data.data?.data;
        if (!coverage) return;
        // Debug-gated, and each axis goes out as ONE string: the console
        // collapses an object past a handful of keys, which hid `pending` and
        // `failed` behind an ellipsis and made frames that differ only in those
        // look identical in the log.
        logger.debug(`${label}: coverage frame`, {
          state: coverage.indexingState,
          buckets: `${coverage.indexed}|${coverage.skipped}|${coverage.stale}|${coverage.pending}|${coverage.failed}`,
          retries: `${coverage.retryingSoon}|${coverage.retryingDaily}|${coverage.parked}`,
          inFlight: coverage.indexingInFlight,
          estimatedReindexSeconds: coverage.estimatedReindexSeconds,
          receivedAt: new Date().toISOString(),
        });
        onFrameRef.current(client.cache, coverage);
      },
      onError: (error: ErrorLike) => {
        // Without this a cable failure is silent and the card just looks frozen —
        // exactly the symptom this subscription exists to remove.
        logger.warn(`${label}: coverage subscription error`, {
          error: error.message,
        });
      },
    }),
    [label],
  );
}
